import React, { createContext, useContext, useEffect, useState } from "react";
import * as Y from "yjs";
import * as awarenessProtocol from "y-protocols/awareness";
import { IndexeddbPersistence } from "y-indexeddb";
import { createSocketCollabProvider } from "pages/modules/kb/helpers/socketCollabProvider";
import { SocketCollabProviderInstance } from "shared/types";
interface YjsContextValue {
  ydoc: Y.Doc;
  awareness: awarenessProtocol.Awareness;
  provider: SocketCollabProviderInstance | null;
}

const YjsCollaborationContext = createContext<YjsContextValue | null>(null);

interface YjsCollaborationProviderProps {
  children: React.ReactNode;
  kbRecordId: string;
  articleId: string;
  snapshotId: string;
  userId: string;
  userName: string;
  userColor: string;
  userAvatar?: string | null;
  mspId: string;
  isEditable: boolean;
}

export function YjsCollaborationProvider({
  children,
  kbRecordId,
  articleId,
  snapshotId,
  userId,
  userName,
  userColor,
  userAvatar,
  mspId,
  isEditable,
}: YjsCollaborationProviderProps) {
  const [ydoc] = useState(() => new Y.Doc());
  const [awareness] = useState(() => new awarenessProtocol.Awareness(ydoc));
  const [provider, setProvider] = useState<SocketCollabProviderInstance | null>(
    null,
  );

  useEffect(() => {
    // Initialize Socket.IO provider only if editable
    let socketProvider: SocketCollabProviderInstance | null = null;
    if (isEditable) {
      socketProvider = createSocketCollabProvider(ydoc, awareness, {
        snapshotId,
        articleId,
        kbRecordId,
        mspId,
        userId,
        userName,
        userColor,
        userAvatar,
      });
      setProvider(socketProvider);
    }

    // Cleanup on unmount
    return () => {
      if (socketProvider) {
        socketProvider.destroy();
      }
      ydoc.destroy();
    };
  }, [snapshotId, kbRecordId, mspId, userId, ydoc, awareness, isEditable]);

  return (
    <YjsCollaborationContext.Provider value={{ ydoc, awareness, provider }}>
      {children}
    </YjsCollaborationContext.Provider>
  );
}

export function useYjsCollaboration() {
  const context = useContext(YjsCollaborationContext);
  if (!context) {
    throw new Error(
      "useYjsCollaboration must be used within YjsCollaborationProvider",
    );
  }
  return context;
}
